import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import { useRefreshHubSessionOnMount } from '../../../hooks/useRefreshHubSessionOnMount.js';
import { buildApiUrl, freshFetchInit } from '../../../utils/apiBase.js';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { formatYen } from '../../../utils/licenseUtils.js';
import { BILLING_DATA_CHANGED_EVENT } from '../../../utils/hubBillingEvents.js';

/**
 * 決済・請求ページ
 * Stripe連携のスケルトン実装
 */
const BillingPage = () => {
  const { user, onRefreshSession } = useOutletContext();
  const canAccessBilling = user?.role === 'doctor' || user?.role === 'admin';
  const { effectiveIsBeta, isHubSettingsReady } = useBeta();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'history' | 'methods'
  useRefreshHubSessionOnMount();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(true);
  const [billingSummaryError, setBillingSummaryError] = useState('');
  const { showToast } = useToast();
  const hasLoadedBillingSummary = useRef(false);

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (err) {
      const error = new Error('データの読み込みに失敗しました。時間をおいて再度お試しください。');
      error.cause = err;
      throw error;
    }
  };

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    setInvoicesError('');
    try {
      const response = await fetch(buildApiUrl('/api/stripe/invoices'), freshFetchInit({
        method: 'GET',
      }));
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || '請求履歴の取得に失敗しました');
      }
      setInvoices(data.invoices || []);
    } catch (err) {
      setInvoicesError(err.message || '請求履歴の取得に失敗しました');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');
    try {
      const response = await fetch(buildApiUrl('/api/stripe/payment-methods'), freshFetchInit({
        method: 'GET',
      }));
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || '支払い方法の取得に失敗しました');
      }
      const list = data.paymentMethods || [];
      console.log('[Billing] Stripe 支払い方法取得結果:', {
        count: list.length,
        paymentMethods: list,
        rawSuccess: data.success
      });
      setPaymentMethods(list);
    } catch (err) {
      setPaymentMethodsError(err.message || '支払い方法の取得に失敗しました');
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const fetchBillingSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setBillingSummaryLoading(true);
      setBillingSummaryError('');
    }
    try {
      const response = await fetch(buildApiUrl('/api/stripe/billing-summary'), freshFetchInit({
        method: 'GET',
      }));
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || '請求サマリーの取得に失敗しました');
      }
      setBillingSummary(data.summary || null);
      setBillingSummaryError('');
      hasLoadedBillingSummary.current = true;
    } catch (err) {
      setBillingSummaryError(err.message || '請求サマリーの取得に失敗しました');
      if (!silent) {
        setBillingSummary(null);
      }
    } finally {
      if (!silent) {
        setBillingSummaryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!canAccessBilling || effectiveIsBeta) return;
    fetchInvoices();
    fetchPaymentMethods();
    fetchBillingSummary({ silent: false });
  }, [canAccessBilling, effectiveIsBeta, fetchBillingSummary]);

  useEffect(() => {
    if (!canAccessBilling || effectiveIsBeta) return;
    if (location.pathname !== '/billing') return;
    if (!hasLoadedBillingSummary.current) return;
    fetchBillingSummary({ silent: true });
  }, [location.pathname, canAccessBilling, effectiveIsBeta, fetchBillingSummary]);

  useEffect(() => {
    if (!canAccessBilling || effectiveIsBeta) return;

    const onBillingDataChanged = () => {
      fetchBillingSummary({ silent: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/billing') {
        fetchBillingSummary({ silent: true });
      }
    };

    window.addEventListener(BILLING_DATA_CHANGED_EVENT, onBillingDataChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener(BILLING_DATA_CHANGED_EVENT, onBillingDataChanged);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canAccessBilling, effectiveIsBeta, location.pathname, fetchBillingSummary]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'overview' || tab === 'history' || tab === 'methods') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Portal から戻った直後: セッションを再取得して hasPaymentInfo 等を最新にし、請求・支払い方法も再取得、URL から ?portal=return を除去
  useEffect(() => {
    const portalReturn = searchParams.get('portal') === 'return';
    if (!portalReturn || !onRefreshSession) return;

    (async () => {
      await onRefreshSession();
      fetchInvoices();
      fetchPaymentMethods();
      fetchBillingSummary({ silent: true });
      showToast('billing.portal.return');
      const next = new URLSearchParams(searchParams);
      next.delete('portal');
      setSearchParams(next, { replace: true });
    })();
  }, [searchParams, onRefreshSession, setSearchParams, showToast]);

  const openPortal = async (returnUrl) => {
    setPortalLoading(true);
    setPortalError('');
    const redirectUrl = returnUrl || `${window.location.origin}/billing?portal=return`;
    try {
      const response = await fetch(buildApiUrl('/api/stripe/portal-session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ returnUrl: redirectUrl })
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || '支払い設定画面を開けませんでした。時間をおいて再度お試しください。');
      }
      window.location.href = data.url;
    } catch (err) {
      setPortalError(err.message || '支払い設定画面を開けませんでした。時間をおいて再度お試しください。');
      showToast('billing.portal.error', {
        message: err.message || '支払い設定画面を開けませんでした。時間をおいて再度お試しください。'
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const getPaymentMethodBadge = (method) => {
    if (method.type === 'card') return method.brand?.toUpperCase() || 'CARD';
    if (method.type === 'link') return 'LINK';
    return (method.displayName || method.type || 'PM').toUpperCase();
  };

  const getPaymentMethodTitle = (method) => {
    if (method.type === 'card') {
      return method.last4 ? `•••• •••• •••• ${method.last4}` : 'カード';
    }
    return method.displayName || '支払い方法';
  };

  const getPaymentMethodSubText = (method) => {
    if (method.type === 'card') {
      return method.expMonth && method.expYear
        ? `有効期限: ${method.expMonth}/${method.expYear}`
        : 'カード情報を登録済み';
    }
    return '登録済み';
  };

  if (!isHubSettingsReady) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">決済・請求</h1>
        </div>
        <div className="hub-main-content hub-enter-panel" data-panel-index="0" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-2xl)' }}>
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (effectiveIsBeta) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">決済・請求</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0">
            <p style={{ marginBottom: 'var(--hub-space-lg)', color: 'var(--hub-text-secondary)' }}>
              β版期間中はご利用いただけません。
            </p>
            <Link to="/dashboard" className="hub-btn hub-btn-primary">
              ダッシュボードへ戻る
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (!canAccessBilling) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">決済・請求</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0">
            <p style={{ marginBottom: 'var(--hub-space-lg)', color: 'var(--hub-text-secondary)' }}>
              決済・請求の確認は医師または管理者のみ可能です。
            </p>
            <Link to="/dashboard" className="hub-btn hub-btn-primary">
              ダッシュボードへ戻る
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">決済・請求</h1>
      </div>

      <div className="hub-main-content">
        {/* タブ */}
        <div className="hub-enter-panel hub-billing-tabs" data-panel-index="0" style={{ 
          display: 'flex',
          gap: 'var(--hub-space-lg)',
          marginBottom: 'var(--hub-space-2xl)',
          borderBottom: '1px solid var(--hub-border)'
        }}>
          {[
            { id: 'overview', label: '概要' },
            { id: 'history', label: '請求履歴' },
            { id: 'methods', label: '支払い方法' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                const next = new URLSearchParams(searchParams);
                next.set('tab', tab.id);
                setSearchParams(next, { replace: true });
              }}
              className="billing-tab"
              style={{
                position: 'relative',
                padding: '0.5rem 0.25rem',
                paddingBottom: 'var(--hub-space-md)',
                border: 'none',
                background: 'transparent',
                color: activeTab === tab.id ? 'var(--hub-text-primary)' : 'var(--hub-text-secondary)',
                fontWeight: 500,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.querySelector('.tab-underline').style.transform = 'scaleX(1)';
                  e.currentTarget.querySelector('.tab-underline').style.opacity = '0.5';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.querySelector('.tab-underline').style.transform = 'scaleX(0)';
                  e.currentTarget.querySelector('.tab-underline').style.opacity = '0';
                }
              }}
            >
              {tab.label}
              <span 
                className="tab-underline"
                style={{
                  position: 'absolute',
                  bottom: '-1px',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'var(--hub-accent-primary)',
                  transform: activeTab === tab.id ? 'scaleX(1)' : 'scaleX(0)',
                  opacity: activeTab === tab.id ? 1 : 0,
                  transition: 'transform 0.3s ease, opacity 0.3s ease'
                }}
              />
            </button>
          ))}
        </div>

        {/* 概要タブ */}
        {activeTab === 'overview' && (
          <>
            {/* 請求サマリー */}
            <div 
              className="hub-card hub-enter-panel animate-fade-in-up stagger-1"
              data-panel-index="1"
              style={{ 
                background: 'var(--hub-bg-secondary)',
                marginBottom: 'var(--hub-space-xl)'
              }}
            >
              {billingSummaryLoading && !billingSummary ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-xl)' }}>
                  <LoadingSpinner />
                </div>
              ) : !billingSummary && billingSummaryError ? (
                <div style={{ color: 'var(--hub-text-secondary)', fontSize: '0.875rem' }}>
                  {billingSummaryError}
                </div>
              ) : (() => {
                const summary = billingSummary || {
                  monthlyRecurringTotalExcludingTax: 0,
                  billableModuleCount: 0,
                  canceledUsableModuleCount: 0,
                  canceledUsableItems: [],
                  pendingBillingSetupItems: [],
                  nextCharge: null,
                };
                const nextCharge = summary.nextCharge;
                const monthlyRecurringTotalExcludingTax =
                  summary.monthlyRecurringTotalExcludingTax ?? 0;
                const nextChargeAmount = nextCharge?.taxBasis === 'including_tax'
                  ? nextCharge.finalizedAmountIncludingTax
                  : nextCharge?.estimatedAmountExcludingTax;

                return (
                  <>
                    {billingSummaryError && (
                      <div style={{
                        marginBottom: 'var(--hub-space-md)',
                        padding: 'var(--hub-space-sm) var(--hub-space-md)',
                        borderRadius: 'var(--hub-radius-md)',
                        background: 'var(--hub-danger-light, rgba(220, 38, 38, 0.08))',
                        color: 'var(--hub-danger)',
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                      }}>
                        {billingSummaryError}（表示は最後に取得できた内容です）
                      </div>
                    )}
                    <div className="hub-billing-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--hub-space-xl)', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ 
                          fontSize: '0.8125rem',
                          color: 'var(--hub-text-muted)',
                          marginBottom: 'var(--hub-space-xs)'
                        }}>
                          月額料金（定額）
                        </div>
                        <div style={{ 
                          fontFamily: 'var(--hub-font-display)',
                          fontSize: '1.75rem',
                          fontWeight: 800,
                          color: 'var(--hub-text-primary)'
                        }}>
                          {formatYen(monthlyRecurringTotalExcludingTax)}<span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--hub-text-muted)', marginLeft: '4px' }}>/月（税別）</span>
                        </div>
                        {nextCharge && (nextChargeAmount > 0 || nextCharge.isImmediate) && (
                          <div style={{
                            marginTop: 'var(--hub-space-sm)',
                            fontSize: '0.8125rem',
                            color: 'var(--hub-text-secondary)',
                            lineHeight: 1.5,
                          }}>
                            {nextCharge.isImmediate ? '未払い請求' : '次回請求'}{' '}
                            <span style={{ fontWeight: 600, color: nextCharge.isImmediate ? 'var(--hub-danger)' : 'var(--hub-text-primary)' }}>
                              {formatYen(nextChargeAmount ?? 0)}
                            </span>
                            <span style={{ color: 'var(--hub-text-muted)' }}>
                              {nextCharge.taxBasis === 'including_tax' ? '（税込）' : '（税別）'}
                            </span>
                            {nextCharge.dateLabel && (
                              <span style={{ color: 'var(--hub-text-muted)' }}>
                                {' '}（{nextCharge.dateLabel}）
                              </span>
                            )}
                            {nextCharge.includesProration && !nextCharge.isImmediate && (
                              <span style={{ color: 'var(--hub-text-muted)' }}> · 日割り含む</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ 
                        display: 'flex',
                        gap: 'var(--hub-space-xl)',
                        fontSize: '0.875rem',
                        flexWrap: 'wrap',
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: 'var(--hub-text-muted)', marginBottom: '4px' }}>契約中</div>
                          <div style={{ fontWeight: 700, color: 'var(--hub-text-primary)' }}>{summary.billableModuleCount || 0} モジュール</div>
                        </div>
                        {summary.canceledUsableModuleCount > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: 'var(--hub-text-muted)', marginBottom: '4px' }}>解約済（利用可）</div>
                            <div style={{ fontWeight: 700, color: 'var(--hub-text-secondary)' }}>{summary.canceledUsableModuleCount} モジュール</div>
                          </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: 'var(--hub-text-muted)', marginBottom: '4px' }}>次回請求日</div>
                          <div style={{ fontWeight: 600, color: 'var(--hub-text-secondary)' }}>
                            {nextCharge?.isImmediate ? '至急' : (nextCharge?.dateLabel || '—')}
                          </div>
                        </div>
                      </div>
                    </div>
                    {summary.additionalCharges?.length > 0 && (
                      <div style={{
                        marginTop: 'var(--hub-space-lg)',
                        paddingTop: 'var(--hub-space-md)',
                        borderTop: '1px solid var(--hub-border-light)',
                      }}>
                        <div style={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: 'var(--hub-text-muted)',
                          marginBottom: 'var(--hub-space-sm)',
                        }}>
                          その他の請求予定
                        </div>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--hub-space-xs)',
                          fontSize: '0.8125rem',
                          color: 'var(--hub-text-secondary)',
                        }}>
                          {summary.additionalCharges.map((charge) => (
                            <div key={`${charge.dateLabel}-${charge.estimatedAmountExcludingTax ?? charge.finalizedAmountIncludingTax}-${charge.kind}`}>
                              {charge.dateLabel}：{formatYen(
                                charge.taxBasis === 'including_tax'
                                  ? (charge.finalizedAmountIncludingTax ?? 0)
                                  : (charge.estimatedAmountExcludingTax ?? 0)
                              )}{charge.taxBasis === 'including_tax' ? '（税込）' : '（税別）'}
                              {charge.includesProration && '（日割り含む）'}
                              {charge.lineItems?.length > 1 && (
                                <span style={{ color: 'var(--hub-text-muted)' }}>
                                  {' '}— {charge.lineItems.map((item) => item.moduleName).join('、')}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {summary.pendingBillingSetupItems?.length > 0 && (
                      <div style={{
                        marginTop: 'var(--hub-space-lg)',
                        paddingTop: 'var(--hub-space-md)',
                        borderTop: '1px solid var(--hub-border-light)',
                        fontSize: '0.8125rem',
                        color: 'var(--hub-text-secondary)',
                        lineHeight: 1.6,
                      }}>
                        <div style={{ fontWeight: 600, color: 'var(--hub-text-muted)', marginBottom: 'var(--hub-space-xs)' }}>
                          請求スケジュール未確定
                        </div>
                        {summary.pendingBillingSetupItems.map((item) => (
                          <div key={item.licenseName || item.moduleName}>
                            {item.moduleName}：試用中のため請求日は未確定です（試用終了 {item.trialEndLabel}）
                          </div>
                        ))}
                      </div>
                    )}
                    {summary.canceledUsableItems?.length > 0 && (
                      <div style={{
                        marginTop: 'var(--hub-space-lg)',
                        paddingTop: 'var(--hub-space-md)',
                        borderTop: '1px solid var(--hub-border-light)',
                        fontSize: '0.8125rem',
                        color: 'var(--hub-text-secondary)',
                        lineHeight: 1.6,
                      }}>
                        {summary.canceledUsableItems.map((item) => (
                          <div key={item.licenseName || item.moduleName}>
                            {item.moduleName}：解約済・{item.usableUntilLabel}まで利用可（請求なし）
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* 支払い設定画面（Stripe Customer Portal） */}
            <div className="hub-card hub-enter-panel animate-fade-in-up stagger-2" data-panel-index="2" style={{ 
              marginBottom: 'var(--hub-space-xl)'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--hub-space-lg)'
              }}>
                <div>
                  <h3 style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '4px' }}>
                    支払い管理
                  </h3>
                  <p style={{ 
                    color: 'var(--hub-text-muted)',
                    fontSize: '0.8125rem',
                    margin: 0
                  }}>
                    支払い方法の変更や請求書・領収書の確認は、支払い設定画面から行えます
                  </p>
                </div>
                <button
                  className="hub-btn hub-btn-secondary"
                  disabled={portalLoading}
                  onClick={() => openPortal()}
                  style={{ flexShrink: 0 }}
                >
                  {portalLoading ? <LoadingSpinner size="small" /> : '支払い設定を開く'}
                </button>
              </div>
              {portalError && (
                <div style={{ marginTop: 'var(--hub-space-sm)', color: 'var(--hub-danger)', fontSize: '0.8125rem' }}>
                  {portalError}
                </div>
              )}
            </div>

            {/* 料金のご案内 */}
            <div className="hub-enter-panel animate-fade-in-up stagger-3" data-panel-index="3" style={{ 
              background: 'var(--hub-bg-tertiary)',
              borderRadius: 'var(--hub-radius-md)',
              padding: 'var(--hub-space-lg)',
              border: '1px solid var(--hub-border-light)'
            }}>
              <h4 style={{ 
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--hub-text-primary)',
                marginBottom: 'var(--hub-space-md)'
              }}>
                料金のご案内
              </h4>
              <div style={{ 
                display: 'grid',
                gap: 'var(--hub-space-sm)',
                fontSize: '0.8125rem',
                color: 'var(--hub-text-secondary)',
                lineHeight: 1.6
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)' }}>
                  <span style={{ color: 'var(--hub-accent-primary)', flexShrink: 0 }}>•</span>
                  <span>試用期間中はいつでも解約でき、請求は発生しません</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)' }}>
                  <span style={{ color: 'var(--hub-accent-primary)', flexShrink: 0 }}>•</span>
                  <span>試用終了後、当月分は日割りで即時決済されます</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)' }}>
                  <span style={{ color: 'var(--hub-accent-primary)', flexShrink: 0 }}>•</span>
                  <span>翌月以降は毎月1日に合算請求となります</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 請求履歴タブ */}
        {activeTab === 'history' && (
          <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0">
            <div className="hub-billing-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--hub-space-lg)' }}>
              <h3 style={{ fontWeight: 700, margin: 0 }}>
                請求履歴
              </h3>
              <button
                className="hub-btn hub-btn-secondary"
                disabled={portalLoading}
                onClick={() => openPortal()}
              >
                請求書・領収書を発行
              </button>
            </div>

            {invoicesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-xl)' }}>
                <LoadingSpinner size="large" />
              </div>
            ) : invoicesError ? (
              <div style={{ color: 'var(--hub-text-secondary)' }}>
                {invoicesError}
              </div>
            ) : invoices.length === 0 ? (
              <div style={{ color: 'var(--hub-text-secondary)' }}>
                請求履歴がありません。
              </div>
            ) : (
              <>
                <div className="hub-invoice-table-wrap">
                  <table className="hub-invoice-table" style={{ 
                    width: '100%',
                    borderCollapse: 'collapse'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--hub-border)' }}>
                        <th style={{ textAlign: 'left', padding: 'var(--hub-space-md)', fontWeight: 600, color: 'var(--hub-text-muted)', fontSize: '0.8125rem' }}>
                          請求書番号
                        </th>
                        <th style={{ textAlign: 'left', padding: 'var(--hub-space-md)', fontWeight: 600, color: 'var(--hub-text-muted)', fontSize: '0.8125rem' }}>
                          日付
                        </th>
                        <th style={{ textAlign: 'left', padding: 'var(--hub-space-md)', fontWeight: 600, color: 'var(--hub-text-muted)', fontSize: '0.8125rem' }}>
                          ステータス
                        </th>
                        <th style={{ textAlign: 'right', padding: 'var(--hub-space-md)', fontWeight: 600, color: 'var(--hub-text-muted)', fontSize: '0.8125rem' }}>
                          金額（税込）
                        </th>
                        <th style={{ textAlign: 'right', padding: 'var(--hub-space-md)' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} style={{ borderBottom: '1px solid var(--hub-border-light)' }}>
                          <td style={{ padding: 'var(--hub-space-md)', fontFamily: 'var(--hub-font-mono)', fontSize: '0.875rem' }}>
                            {invoice.number || invoice.id}
                          </td>
                          <td style={{ padding: 'var(--hub-space-md)', color: 'var(--hub-text-secondary)' }}>
                            {new Date(invoice.created * 1000).toLocaleDateString()}
                          </td>
                          <td style={{ padding: 'var(--hub-space-md)' }}>
                            <span style={{
                              background: invoice.status === 'paid' ? 'var(--hub-success-light)' : 'var(--hub-warning-light)',
                              color: invoice.status === 'paid' ? 'var(--hub-success)' : 'var(--hub-warning)',
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              {invoice.status === 'paid' ? '支払済' : invoice.status}
                            </span>
                          </td>
                          <td style={{ padding: 'var(--hub-space-md)', textAlign: 'right', fontWeight: 600 }}>
                            ¥{((invoice.status === 'paid'
                              ? invoice.amountPaidIncludingTax
                              : invoice.amountDueIncludingTax) ?? 0).toLocaleString()}
                          </td>
                          <td style={{ padding: 'var(--hub-space-md)', textAlign: 'right' }}>
                            <button
                              className="hub-btn hub-btn-ghost hub-btn-sm"
                              onClick={() => openPortal()}
                            >
                              請求内容を確認
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="hub-invoice-cards">
                  {invoices.map((invoice) => (
                    <article key={invoice.id} className="hub-invoice-card">
                      <div className="hub-invoice-card-header">
                        <span className="hub-invoice-card-number">{invoice.number || invoice.id}</span>
                        <span style={{
                          background: invoice.status === 'paid' ? 'var(--hub-success-light)' : 'var(--hub-warning-light)',
                          color: invoice.status === 'paid' ? 'var(--hub-success)' : 'var(--hub-warning)',
                          padding: '4px 10px',
                          borderRadius: 'var(--hub-radius-full)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          {invoice.status === 'paid' ? '支払済' : invoice.status}
                        </span>
                      </div>
                      <dl className="hub-invoice-card-meta">
                        <div>
                          <dt>日付</dt>
                          <dd>{new Date(invoice.created * 1000).toLocaleDateString()}</dd>
                        </div>
                        <div>
                          <dt>金額（税込）</dt>
                          <dd className="hub-invoice-card-amount">¥{((invoice.status === 'paid'
                            ? invoice.amountPaidIncludingTax
                            : invoice.amountDueIncludingTax) ?? 0).toLocaleString()}</dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="hub-btn hub-btn-ghost hub-btn-sm hub-invoice-card-action"
                        onClick={() => openPortal()}
                      >
                        請求内容を確認
                      </button>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* 支払い方法タブ */}
        {activeTab === 'methods' && (
          <>
            <div className="hub-billing-section-header hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--hub-space-lg)' }}>
              <h3 style={{ fontWeight: 700, margin: 0 }}>
                支払い方法
              </h3>
              <button
                className="hub-btn hub-btn-secondary"
                disabled={portalLoading}
                onClick={() => openPortal()}
              >
                支払い方法を追加
              </button>
            </div>

            {paymentMethodsLoading ? (
              <div className="hub-enter-panel" data-panel-index="1" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-xl)' }}>
                <LoadingSpinner size="large" />
              </div>
            ) : paymentMethodsError ? (
              <div className="hub-enter-panel" data-panel-index="1" style={{ color: 'var(--hub-text-secondary)' }}>
                {paymentMethodsError}
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="hub-enter-panel" data-panel-index="1" style={{ color: 'var(--hub-text-secondary)' }}>
                支払い方法が登録されていません。
              </div>
            ) : (
              <div className="hub-enter-panel" data-panel-index="1" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-md)' }}>
                {paymentMethods.map((method) => (
                  <div key={method.id} className="hub-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hub-space-lg)' }}>
                        <div style={{
                          width: '56px',
                          height: '36px',
                          background: 'linear-gradient(135deg, #1a1f71 0%, #2a3990 100%)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700
                        }}>
                          {getPaymentMethodBadge(method)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{getPaymentMethodTitle(method)}</div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                            {getPaymentMethodSubText(method)}
                          </div>
                        </div>
                      </div>
                      {method.isDefault && (
                        <span style={{
                          background: 'var(--hub-accent-primary-light)',
                          color: 'var(--hub-accent-primary)',
                          padding: '4px 10px',
                          borderRadius: 'var(--hub-radius-full)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          デフォルト
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default BillingPage;

